/**
 * Product Families API Hook
 *
 * Provides API methods for managing product families.
 * Used by both Super Admin (full CRUD) and Vendors (read-only for enabled families).
 */

import { routes } from '../../assets/constants';
import { useApi } from './use-api.hook';
import { ApiMethod } from '../models/api-types.type';
import {
  ProductFamily,
  CreateProductFamilyPayload,
  UpdateProductFamilyPayload,
} from '../models/product-family.model';

export interface ProductFamiliesApiResponse {
  data: ProductFamily[];
  total?: number;
}

export interface ProductFamilyApiResponse {
  data: ProductFamily;
}

export interface VendorFamiliesUpdatePayload {
  enabledFamilyIds: string[];
}

export const useProductFamiliesApi = () => {
  const { sendProtectedRequest } = useApi();

  // ==========================================================================
  // Family CRUD Operations (Super Admin)
  // ==========================================================================

  /**
   * Get all product families
   * Super Admin: Gets all families
   * Vendor: Gets only enabled families for their vendor
   */
  const findAllFamilies = async (
    params?: { activeOnly?: boolean }
  ): Promise<ProductFamily[]> => {
    const queryParams = new URLSearchParams();
    if (params?.activeOnly) {
      queryParams.append('activeOnly', 'true');
    }
    const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await sendProtectedRequest(
      ApiMethod.GET,
      `${routes.productFamilies.findAll}${query}`
    );
    return response || [];
  };

  /**
   * Get a single product family by ID
   */
  const findOneFamily = async (id: string): Promise<ProductFamily | null> => {
    const response = await sendProtectedRequest(
      ApiMethod.GET,
      routes.productFamilies.findOne(id)
    );
    return response || null;
  };

  /**
   * Create a new product family (Super Admin only)
   */
  const createFamily = async (
    payload: CreateProductFamilyPayload
  ): Promise<ProductFamily> => {
    const response = await sendProtectedRequest(
      ApiMethod.POST,
      routes.productFamilies.create,
      payload
    );
    return response;
  };

  /**
   * Update a product family (Super Admin only)
   */
  const updateFamily = async (
    id: string,
    payload: UpdateProductFamilyPayload
  ): Promise<ProductFamily> => {
    const response = await sendProtectedRequest(
      ApiMethod.PATCH,
      routes.productFamilies.patch(id),
      payload
    );
    return response;
  };

  /**
   * Delete a product family (Super Admin only)
   * Note: System families cannot be deleted
   */
  const deleteFamily = async (id: string): Promise<void> => {
    await sendProtectedRequest(ApiMethod.DELETE, routes.productFamilies.deleteOne(id));
  };

  /**
   * Reorder product families (Super Admin only)
   */
  const reorderFamilies = async (orderedIds: string[]): Promise<void> => {
    await sendProtectedRequest(ApiMethod.POST, routes.productFamilies.reorder, {
      orderedIds,
    });
  };

  // ==========================================================================
  // Vendor-Family Assignment (Super Admin)
  // ==========================================================================

  /**
   * Get families enabled for a specific vendor
   */
  const getVendorFamilies = async (vendorId: string): Promise<ProductFamily[]> => {
    const response = await sendProtectedRequest(
      ApiMethod.GET,
      routes.productFamilies.vendorFamilies(vendorId)
    );
    return response || [];
  };

  /**
   * Update families enabled for a specific vendor (Super Admin only)
   */
  const updateVendorFamilies = async (
    vendorId: string,
    payload: VendorFamiliesUpdatePayload
  ): Promise<void> => {
    await sendProtectedRequest(
      ApiMethod.PATCH,
      routes.productFamilies.vendorFamilies(vendorId),
      payload
    );
  };

  // ==========================================================================
  // Helper Methods
  // ==========================================================================

  /**
   * Find a family by its slug
   */
  const findFamilyBySlug = async (slug: string): Promise<ProductFamily | null> => {
    const families = await findAllFamilies();
    return families.find(f => f.slug === slug) || null;
  };

  /**
   * Get the default family (Surface Products)
   */
  const getDefaultFamily = async (): Promise<ProductFamily | null> => {
    const families = await findAllFamilies();
    return families.find(f => f.isDefault) || families[0] || null;
  };

  return {
    // Family CRUD
    findAllFamilies,
    findOneFamily,
    createFamily,
    updateFamily,
    deleteFamily,
    reorderFamilies,

    // Vendor-Family assignment
    getVendorFamilies,
    updateVendorFamilies,

    // Helpers
    findFamilyBySlug,
    getDefaultFamily,
  };
};
